/**
 * URI helper utilities for parsing and handling feature URIs
 */

/**
 * Extracts the numeric ID from a feature URI
 * @param {string} uri - Feature URI (e.g., "/features/123" or "/features/123/")
 * @returns {string|null} - The feature ID or null if not found
 *
 * @example
 * extractFeatureId('/features/1000013363') // '1000013363'
 * extractFeatureId('/features/123/') // '123'
 * extractFeatureId('/features/123?v=2') // '123'
 */
export const extractFeatureId = (uri) => {
  if (!uri || typeof uri !== 'string') return null

  // Handle trailing slashes and query params
  const match = uri.match(/\/features\/(\d+)/i)
  return match ? match[1] : null
}

/**
 * Extracts base feature ID from a URI that may include revision paths
 * @param {string} uri - URI that may be /features/123 or /features/123/revisions/456
 * @returns {string|null} - The base feature ID
 *
 * @example
 * extractBaseFeatureId('/features/1000013363/revisions/rev-2') // '1000013363'
 * extractBaseFeatureId('/features/1000013363') // '1000013363'
 */
export const extractBaseFeatureId = (uri) => {
  if (!uri || typeof uri !== 'string') return null

  // Extract the first numeric ID after /features/
  const match = uri.match(/\/features\/(\d+)/i)
  return match ? match[1] : null
}

/**
 * Creates a stable row key for feature table rows
 * Combines base feature ID with revision to ensure uniqueness
 * @param {Object} record - Feature record
 * @param {string} record.uri - Feature URI
 * @param {string} record.id - Feature ID
 * @param {string} record.revision - Feature revision
 * @returns {string} - Stable row key
 *
 * @example
 * createFeatureRowKey({ uri: '/features/123', revision: '2.0' }) // '123-2.0'
 * createFeatureRowKey({ id: '456', revision: '1.0' }) // '456-1.0'
 */
export const createFeatureRowKey = (record) => {
  // Try to extract base ID from URI first
  const baseId = extractBaseFeatureId(record.uri) || record.id

  // Include revision for uniqueness when same feature appears with different revisions
  const revision = record.revision || ''

  return `${baseId}-${revision}`
}
